import React, { useEffect, useState } from 'react';
import InputField from 'common/atoms/inputField/InputField';
import MainImage from 'pages/wholeme/molecules/mainImage/MainImage';
import ProfileInputPage from 'pages/wholeme/molecules/inputPage/ProfileInputPage';
import { useFeatureFlagsContext } from 'providers/featureflags-provider';
import appStrings from '../../../../../common/app_strings';
import { useAppState } from '../../../../../providers/app-state/app-state-provider';

const { wholeMe: { wholeMeBirthdayIntake } } = appStrings;

const inputContainerStyles = {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
};

const inputStyles = {
  '& fieldset': { border: 'none' },
  '& .MuiInputBase-root': {
    borderRadius: '0px',
    '& .MuiInputBase-input::placeholder': {
      color: '#9C9C9C',
    },
  },
};

const dayCountMonthLookup = {
  '01': 31,
  '02': 29,
  '03': 31,
  '04': 30,
  '05': 31,
  '06': 30,
  '07': 31,
  '08': 31,
  '09': 30,
  1: 31,
  2: 29,
  3: 31,
  4: 30,
  5: 31,
  6: 30,
  7: 31,
  8: 31,
  9: 30,
  10: 31,
  11: 30,
  12: 31,
};

const checkForLeapYear = (year) => {
  if (year % 4 === 0) {
    if (year % 100 === 0) {
      if (year % 400 === 0) {
        return true;
      }
      return false;
    }
    return true;
  }
  return false;
};

const ProfileIdentityIntakeBirthdayPage = ({
  paginationMeta,
  nextOnClick,
  prevOnClick,
  wholeMeClient,
  exitIconOnClickOverride,
}) => {
  const [dayState, setDayState] = useState();
  const [monthState, setMonthState] = useState();
  const [yearState, setYearState] = useState();
  const [errorMessage, setErrorMessage] = useState('');
  const { isSaveAndContinueAlwaysEnabled } = useFeatureFlagsContext() || {};
  const { platform } = useAppState();

  useEffect(() => {
    void (async () => {
    const state = await wholeMeClient.fetchBirthdayState();
    const birthdayString = (state?.birthday[0]?.value?.split('/') || ['', '', '']);
    setDayState(birthdayString[1]);
    setMonthState(birthdayString[0]);
    setYearState(birthdayString[2]);
  })();
  }, []);

  const saveBirthday = () => {
    setErrorMessage('');
    if (monthState < 1 || monthState > 12) setErrorMessage('Invalid month');
    else if (dayState < 1 || dayState > dayCountMonthLookup[monthState]) setErrorMessage('Invalid day');
    else if (yearState < 1900) setErrorMessage('Invalid year');
    // eslint-disable-next-line eqeqeq
    else if (dayState == 29 && monthState == 2 && !checkForLeapYear(yearState)) setErrorMessage('Invalid date');
    else {
      wholeMeClient
        .saveBirthdayState([
          {
            category_id: 'identity',
            question_id: 'birthday',
            value: (yearState && monthState && dayState) ? new Date(yearState, (monthState - 1), dayState) : null,
          },
        ])
        .then((res) => {
          const { error } = res || {};
          if (error) setErrorMessage('Invalid Date');
          nextOnClick();
        });
    }
  };

  const mainImageData = {
    imageSrc: './assets/profile/identity/circle-girl-orange-blue-triangles.svg',
    imageAlt: 'smiling girl',
  };

  const isLoading = dayState === undefined && monthState === undefined && yearState === undefined;
  const isCtaDisabled = isSaveAndContinueAlwaysEnabled && !((dayState?.length && monthState?.length && yearState?.length === 4));

  const handleFocusSwitchForward = (value) => {
    if (value.length === 2) {
      // Find next input and focus it
      const inputs = document.querySelectorAll('input');
      const currentIndex = Array.from(inputs).findIndex((input) => input === document.activeElement);
      if (currentIndex < inputs.length - 1) {
        inputs[currentIndex + 1].focus();
      }
    }
  };

  const handleFocusSwitchBackward = (e) => {
    if (e.key === 'Backspace' && !e.target.value) {
      // Find previous input and focus it
      const inputs = document.querySelectorAll('input');
      const currentIndex = Array.from(inputs).findIndex((input) => input === document.activeElement);
      if (currentIndex > 0) {
        inputs[currentIndex - 1].focus();
      }
    }
  };

  return (
    <ProfileInputPage
      paginationMeta={paginationMeta}
      nextOnClick={saveBirthday}
      isCtaDisabled={isCtaDisabled}
      nextButtonIsLoading={isLoading}
      prevOnClick={prevOnClick}
      wholeMeClient={wholeMeClient}
      title={wholeMeBirthdayIntake.title}
      subtitle={wholeMeBirthdayIntake.subtitle}
      backgroundOverlay="/assets/profile/identity/date-of-birth-overlay-3.svg"
      exitIconOnClickOverride={exitIconOnClickOverride}
    >
      <div style={inputContainerStyles}>
        <InputField
          onChange={(value) => {
            setMonthState(value);
            handleFocusSwitchForward(value);
          }}
          placeholder="MM"
          masker="00" // Ensures two digits with leading zero
          fieldValue={monthState}
          stylesOverride={{ width: '96px' }}
          icon="filledDob"
          maxValueLength="2"
          enableNumpad
          error={!!errorMessage && { message: errorMessage }}
          theme="light"
          overrideStyles={{
            ...inputStyles,
            '& .MuiInputBase-root': {
              borderTopLeftRadius: '8px',
              borderBottomLeftRadius: '8px',
            },
          }}
        />
        <InputField
          onChange={(value) => {
            setDayState(value);
            handleFocusSwitchForward(value);
          }}
          onKeyDown={handleFocusSwitchBackward}
          placeholder="DD"
          fieldValue={dayState}
          stylesOverride={{ width: '51px' }}
          isInputFieldContentCentered
          masker="00"
          forcePadding
          maxValueLength="2"
          enableNumpad
          error={!!errorMessage}
          theme="light"
          overrideStyles={inputStyles}
        />
        <InputField
          onChange={(value) => setYearState(value)}
          onKeyDown={handleFocusSwitchBackward}
          placeholder="YYYY"
          fieldValue={yearState}
          stylesOverride={{ width: '89px' }}
          isInputFieldContentCentered
          masker="0000"
          forcePadding
          maxValueLength="4"
          enableNumpad
          error={!!errorMessage}
          theme="light"
          overrideStyles={{
            ...inputStyles,
            '& .MuiInputBase-root': {
              borderTopRightRadius: '8px',
              borderBottomRightRadius: '8px',
            },
          }}
        />
      </div>
      <MainImage
        imageSrc={mainImageData.imageSrc}
        imageAlt={mainImageData.imageAlt}
        imageStylesOverride={{
          paddingLeft: '10%',
          width: platform?.isNativePlatform ? '100%' : 'calc(45% - 80px)',
        }}
      />
    </ProfileInputPage>
  );
};

export default ProfileIdentityIntakeBirthdayPage;
